// PHASE_1 / B3 — the reporting-lines Content component (ADR-014 §2).
// Faz 8.6 / Batch A5 restyles the VISUAL layer to the novem language (a
// StatCard strip, Badge-toned kind/state cells, Avatar subordinate/reporter
// cells in a Card-wrapped table). The page header belongs to OrgContent; the
// create action reaches it through PageHeaderActions.
//
// Lists matrix reporting relationships (keyset, never OFFSET) and offers a
// create modal (dotted/functional/interim/secondment/community_chair) + an "end"
// action on active rows. Solid-line manager_id is NOT edited here — these are
// additive matrix ties (ADR-012). The server runs the cycle-detect /
// redundant-dotted / interim-needs-end-date gates; this surface renders the
// spec-58 error inline. ScopeGate hides the create/end affordances the caller
// can't perform (client convenience only — the route re-checks; ADR-017). No money.

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GitBranch, Share2, Spline } from "lucide-react";

import {
  Avatar,
  Badge,
  Button,
  Card,
  DataTable,
  Empty,
  ErrorState,
  HelpHint,
  Loading,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ResourceForm,
  ScopeGate,
  StatCard,
  type BadgeTone,
  type DataTableColumn,
  type ResourceField,
  type SafeParseValidator,
} from "@novemai/components";

import { EnvelopeError, errorMessage } from "@/api/errors";
import { useKeysetQuery } from "@/api/keyset";
import { formatCountWithMore } from "@/lib/count";
import { PageHeaderActions } from "@/components/page-header-actions";
import { PersonLink } from "@/components/person-link";
import {
  createReportingRelationship,
  endReportingRelationship,
  fetchRelationshipsPage,
  type ReportingRelationshipDraft,
  type ReportingRelationshipRow,
} from "./api";

// SEM-301: the reporting API now carries server-resolved display names
// (subordinate_name / reporter_name; PII-gated). The Avatar cells label with the
// name, falling back to this short-id convention (A1/A2) only when the name is
// null (unresolved or tombstone-redacted).
const shortId = (id: string): string => id.slice(0, 8);

const STATE_TONE: Readonly<Record<string, BadgeTone>> = {
  active: "success",
  ended: "neutral",
  cancelled: "danger",
};

const KIND_TONE: Readonly<Record<string, BadgeTone>> = {
  dotted_line: "info",
  functional: "accent",
  interim: "warning",
  secondment: "warning",
  community_chair: "neutral",
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function makeRelationshipValidator(
  msg: (key: string) => string,
): SafeParseValidator<ReportingRelationshipDraft> {
  return {
    safeParse(input: unknown) {
      const raw = (input ?? {}) as Record<string, unknown>;
      const issues: Array<{ path: string[]; message: string }> = [];

      const subordinate = String(raw["subordinate_employee_id"] ?? "").trim();
      if (subordinate.length === 0) {
        issues.push({ path: ["subordinate_employee_id"], message: msg("org.form_error_subordinate_required") });
      }
      const reporter = String(raw["reporter_employee_id"] ?? "").trim();
      if (reporter.length === 0) {
        issues.push({ path: ["reporter_employee_id"], message: msg("org.form_error_reporter_required") });
      }
      if (subordinate.length > 0 && subordinate === reporter) {
        issues.push({ path: ["reporter_employee_id"], message: msg("org.form_error_self") });
      }
      const kind = String(raw["kind"] ?? "dotted_line");
      const endsAtRaw = String(raw["ends_at"] ?? "").trim();
      const weightRaw = String(raw["weight_percent"] ?? "").trim();
      let weight: number | undefined;
      if (weightRaw.length > 0) {
        const n = Number(weightRaw);
        if (!Number.isFinite(n) || n < 0 || n > 100) {
          issues.push({ path: ["weight_percent"], message: msg("org.form_error_weight_invalid") });
        } else {
          weight = n;
        }
      }

      if (issues.length > 0) return { success: false, error: { issues } };
      return {
        success: true,
        data: {
          subordinate_employee_id: subordinate,
          reporter_employee_id: reporter,
          kind,
          ...(ISO_DATE.test(endsAtRaw) ? { ends_at: endsAtRaw } : {}),
          ...(weight !== undefined ? { weight_percent: weight } : {}),
          ...(String(raw["rationale"] ?? "").trim().length > 0
            ? { rationale: String(raw["rationale"]).trim() }
            : {}),
        },
      };
    },
  };
}

export interface OrgReportingContentProps {
  /** Capability set for the convenience ScopeGate (server is authority). */
  readonly allowedCapabilities?: readonly string[];
}

interface PendingRelationshipIntent {
  readonly fingerprint: string;
  readonly draft: ReportingRelationshipDraft;
  readonly idempotencyKey: string;
}

function isDefinitiveRelationshipError(error: unknown): boolean {
  return error instanceof EnvelopeError && error.httpStatus >= 400 && error.httpStatus < 500;
}

export function OrgReportingContent({
  allowedCapabilities = [],
}: OrgReportingContentProps): React.ReactElement {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const pendingRelationship = useRef<PendingRelationshipIntent | null>(null);

  const query = useKeysetQuery<ReportingRelationshipRow>({
    queryKey: ["org", "reporting-relationships"],
    fetchPage: fetchRelationshipsPage,
  });

  // Honest aggregates over the loaded window ("+" while hasNextPage signals
  // further pages — the dashboard/A1 convention).
  const activeCount = query.items.filter((r) => r.state === "active").length;
  const dottedCount = query.items.filter((r) => r.kind === "dotted_line").length;
  const functionalCount = query.items.filter((r) => r.kind === "functional").length;

  const fields: readonly ResourceField[] = [
    { name: "subordinate_employee_id", label: t("org.form_subordinate"), type: "text", required: true },
    { name: "reporter_employee_id", label: t("org.form_reporter"), type: "text", required: true },
    {
      // interim/secondment are intentionally OMITTED here: they require an
      // approval_request_id (material approval-routing change; spec 68 + DB
      // CHECK) which the approvals routing UI doesn't wire until a later phase.
      // Offering them with no approval_request_id input would always 400. The
      // create-with-approval flow for interim/secondment is a [P1/B3] carryover
      // (see content header note). Column rendering still resolves every kind.
      name: "kind",
      label: t("org.form_kind"),
      type: "select",
      options: [
        { value: "dotted_line", label: t("org.kind.dotted_line") },
        { value: "functional", label: t("org.kind.functional") },
        { value: "community_chair", label: t("org.kind.community_chair") },
      ],
    },
    { name: "ends_at", label: t("org.form_ends_at"), type: "date" },
    { name: "weight_percent", label: t("org.form_weight"), type: "number" },
    { name: "rationale", label: t("org.form_rationale"), type: "textarea" },
  ];

  async function onSubmit(draft: ReportingRelationshipDraft): Promise<void> {
    setSubmitError(null);
    const fingerprint = JSON.stringify(draft);
    const intent =
      pendingRelationship.current?.fingerprint === fingerprint
        ? pendingRelationship.current
        : { fingerprint, draft, idempotencyKey: crypto.randomUUID() };
    pendingRelationship.current = intent;
    try {
      await createReportingRelationship(intent.draft, intent.idempotencyKey);
      pendingRelationship.current = null;
      closeForm();
      query.refetch();
    } catch (err) {
      // Surface the spec-58 error (cycle / redundant / interim-needs-end-date)
      // inline — the server is the authority for these gates.
      if (isDefinitiveRelationshipError(err)) pendingRelationship.current = null;
      setSubmitError(errorMessage(err, t));
    }
  }

  function closeForm(): void {
    setShowForm(false);
    setSubmitError(null);
  }

  async function onEnd(id: string): Promise<void> {
    try {
      await endReportingRelationship(id);
      query.refetch();
    } catch {
      // Refetch leaves the row as-is on failure; the toast adapter surfaces it.
      query.refetch();
    }
  }

  const columns: readonly DataTableColumn<ReportingRelationshipRow>[] = [
    {
      id: "kind",
      header: t("org.reporting_column_kind"),
      cell: (row): React.ReactNode => (
        <Badge tone={KIND_TONE[row.kind] ?? "neutral"}>{t(`org.kind.${row.kind}`)}</Badge>
      ),
    },
    {
      id: "subordinate",
      header: t("org.reporting_column_subordinate"),
      cell: (row): React.ReactNode => (
        <div className="flex items-center gap-2.5">
          <Avatar size={32} alt={row.subordinate_name ?? shortId(row.subordinate_employee_id)} />
          {/* The Avatar already carries the name as its accessible label, so
              only the text is wrapped — wrapping both would announce it twice. */}
          <PersonLink employeeId={row.subordinate_employee_id}>
            <span className="whitespace-nowrap font-mono text-[12px] text-[var(--fg-secondary)]">
              {row.subordinate_name ?? shortId(row.subordinate_employee_id)}
            </span>
          </PersonLink>
        </div>
      ),
    },
    {
      id: "reporter",
      // "Raporlayan" / "Rapor edilen" name the two ENDS of one line and read as
      // near-synonyms in TR; the two avatar cells are drawn identically, so the
      // header text is the only thing that says which end this is.
      header: (
        <span className="inline-flex items-center gap-1">
          {t("org.reporting_column_reporter")}
          <HelpHint
            label={t("common.help_for", { field: t("org.reporting_column_reporter") })}
          >
            {t("org.reporting_column_reporter_help")}
          </HelpHint>
        </span>
      ),
      cell: (row): React.ReactNode => (
        <div className="flex items-center gap-2.5">
          <Avatar size={32} alt={row.reporter_name ?? shortId(row.reporter_employee_id)} />
          <PersonLink employeeId={row.reporter_employee_id}>
            <span className="whitespace-nowrap font-mono text-[12px] text-[var(--fg-secondary)]">
              {row.reporter_name ?? shortId(row.reporter_employee_id)}
            </span>
          </PersonLink>
        </div>
      ),
    },
    {
      id: "state",
      header: t("org.reporting_column_state"),
      cell: (row): React.ReactNode => (
        <Badge tone={STATE_TONE[row.state] ?? "neutral"}>{t(`org.state.${row.state}`)}</Badge>
      ),
    },
    {
      id: "weight",
      header: t("org.reporting_column_weight"),
      align: "end",
      cell: (row): string => row.weight_percent ?? "—",
    },
    {
      id: "actions",
      header: "",
      align: "end",
      cell: (row): React.ReactNode =>
        row.state === "active" ? (
          <ScopeGate required="org.update" allowed={allowedCapabilities}>
            <Button
              variant="danger"
              size="sm"
              onClick={(): void => void onEnd(row.relationship_id)}
            >
              {t("org.reporting_end")}
            </Button>
          </ScopeGate>
        ) : null,
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeaderActions>
        <ScopeGate required="org.create" allowed={allowedCapabilities}>
          <Button variant="primary" onClick={(): void => setShowForm(true)}>
            <GitBranch size={14} aria-hidden="true" />
            {t("org.reporting_add")}
          </Button>
        </ScopeGate>
      </PageHeaderActions>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label={t("org.reporting_kpi_active")}
          value={formatCountWithMore(activeCount, query.hasNextPage)}
          tone="accent"
          icon={<Share2 size={18} />}
        />
        <StatCard
          label={t("org.reporting_kpi_dotted")}
          value={formatCountWithMore(dottedCount, query.hasNextPage)}
          tone="info"
          icon={<Spline size={18} />}
        />
        <StatCard
          label={t("org.reporting_kpi_functional")}
          value={formatCountWithMore(functionalCount, query.hasNextPage)}
          tone="success"
          icon={<GitBranch size={18} />}
        />
      </div>

      <Modal open={showForm} onClose={closeForm}>
        <ModalHeader title={t("org.reporting_add")} />
        {/* The form wraps body AND footer so the footer submit stays a native
            submit of this form; min-h-0 lets the modal body keep scrolling. */}
        <ResourceForm<ReportingRelationshipDraft>
          className="min-h-0"
          fields={fields}
          validator={makeRelationshipValidator(t)}
          onValidSubmit={onSubmit}
          submitLabel={t("org.form_submit")}
          cancel={{ label: t("common.cancel"), onCancel: closeForm }}
          collect={(formData): unknown => Object.fromEntries(formData.entries())}
          renderLayout={({ fields: fieldNodes, actions }): React.ReactNode => (
            <>
              <ModalBody className="space-y-3">
                <p className="text-sm text-[var(--fg-muted)]">
                  {t("org.form_interim_secondment_note")}
                </p>
                {submitError !== null && (
                  <p role="alert" className="text-sm text-[var(--state-danger)]">
                    {submitError}
                  </p>
                )}
                {fieldNodes}
              </ModalBody>
              <ModalFooter>{actions}</ModalFooter>
            </>
          )}
        />
      </Modal>

      {query.isLoading ? (
        <Loading label={t("org.loading")} rows={5} />
      ) : query.isError ? (
        <ErrorState
          title={t("org.error_title")}
          description={errorMessage(query.error, t)}
        />
      ) : query.items.length > 0 ? (
        <Card className="overflow-hidden">
          <DataTable<ReportingRelationshipRow>
            caption={t("org.reporting_title")}
            columns={columns}
            rows={query.items}
            rowKey={(row): string => row.relationship_id}
            hasNextPage={query.hasNextPage}
            onLoadMore={query.fetchNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            loadMoreLabel={t("org.load_more")}
          />
        </Card>
      ) : (
        <Empty
          title={t("org.reporting_empty_title")}
          description={t("org.reporting_empty_description")}
        />
      )}
    </div>
  );
}
